import { useState, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import Small from "../atoms/typography/Small";

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipPortalContent = styled.div<{
  width?: string;
  backgroundColor?: string;
}>`
  max-width: 500px;
  width: ${(props) => props.width || "auto"};
  background-color: ${(props) => props.backgroundColor || "#4d4d4d"};
  color: white;
  text-align: center;
  border-radius: 8px;
  padding: 8px;
  position: fixed;
  z-index: 9999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  pointer-events: none;
`;

interface TooltipProps {
  content?: React.ReactNode;
  text?: string;
  children: React.ReactNode;
  width?: string;
  backgroundColor?: string;
  direction?: "up" | "down" | "left" | "right";
}

const GAP = 8;

function computePosition(
  anchor: DOMRect,
  tooltip: DOMRect,
  preferred: "up" | "down" | "left" | "right",
): React.CSSProperties {
  const { innerWidth: vw, innerHeight: vh } = window;
  const centerX = anchor.left + anchor.width / 2;
  const centerY = anchor.top + anchor.height / 2;

  let dir = preferred;
  if (
    preferred === "up" &&
    anchor.top < tooltip.height + GAP &&
    vh - anchor.bottom > anchor.top
  )
    dir = "down";
  else if (
    preferred === "down" &&
    vh - anchor.bottom < tooltip.height + GAP &&
    anchor.top > vh - anchor.bottom
  )
    dir = "up";
  else if (
    preferred === "left" &&
    anchor.left < tooltip.width + GAP &&
    vw - anchor.right > anchor.left
  )
    dir = "right";
  else if (
    preferred === "right" &&
    vw - anchor.right < tooltip.width + GAP &&
    anchor.left > vw - anchor.right
  )
    dir = "left";

  const clampX = (x: number) =>
    Math.max(GAP, Math.min(x, vw - tooltip.width - GAP));
  const clampY = (y: number) =>
    Math.max(GAP, Math.min(y, vh - tooltip.height - GAP));

  switch (dir) {
    case "down":
      return {
        top: anchor.bottom + GAP,
        left: clampX(centerX - tooltip.width / 2),
      };
    case "left":
      return {
        top: clampY(centerY - tooltip.height / 2),
        left: clampX(anchor.left - tooltip.width - GAP),
      };
    case "right":
      return {
        top: clampY(centerY - tooltip.height / 2),
        left: clampX(anchor.right + GAP),
      };
    default:
      return {
        top: clampY(anchor.top - tooltip.height - GAP),
        left: clampX(centerX - tooltip.width / 2),
      };
  }
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  text,
  width,
  direction = "up",
  backgroundColor,
}) => {
  const [visible, setVisible] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!visible || !tooltipRef.current || !wrapperRef.current) return;
    const anchorRect = wrapperRef.current.getBoundingClientRect();
    const tipRect = tooltipRef.current.getBoundingClientRect();
    setStyle(computePosition(anchorRect, tipRect, direction));
  }, [visible]);

  return (
    <TooltipWrapper
      ref={wrapperRef}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => {
        setVisible(false);
        setStyle({ visibility: "hidden" });
      }}
    >
      {children}
      {visible && (text || content) &&
        ReactDOM.createPortal(
          <TooltipPortalContent
            ref={tooltipRef}
            width={width}
            style={style}
            backgroundColor={backgroundColor}
          >
            {text ? <Small color="white">{text}</Small> : content}
          </TooltipPortalContent>,
          document.body,
        )}
    </TooltipWrapper>
  );
};

export default Tooltip;
